import {
  collection,
  documentId,
  doc,
  getDoc,
  getDocs,
  limit,
  orderBy,
  query,
  startAfter,
  where,
  type DocumentData,
  type QueryDocumentSnapshot,
} from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { firestore, functions } from '../firebase';
import {
  COMMUNITY_CATEGORY_BY_SLUG,
  COMMUNITY_CATEGORIES,
  canViewCommunityThread,
  normalizeCommunityPostQuote,
  type CommunityPost,
  type CommunityReport,
  type CommunityReportOccurrence,
  type CommunityReportReason,
  type CommunityThread,
} from '../shared/community';
import type { ServiceProvider } from '../types/firestore';

const THREADS_COLLECTION = 'communityThreads';
const REPORTS_COLLECTION = 'communityReports';
const SERVICE_PROVIDERS_COLLECTION = 'serviceProviders';
const COMMUNITY_USER_READS_COLLECTION = 'communityUserReads';

export const createCommunityClientRequestId = (): string => {
  const cryptoApi = globalThis.crypto;
  if (typeof cryptoApi?.randomUUID === 'function') {
    return `community_${cryptoApi.randomUUID().replace(/-/g, '_')}`;
  }
  if (typeof cryptoApi?.getRandomValues === 'function') {
    const bytes = new Uint8Array(18);
    cryptoApi.getRandomValues(bytes);
    return `community_${Array.from(bytes, (byte) => byte.toString(16).padStart(2, '0')).join('')}`;
  }
  throw new Error(
    'Ta przeglądarka nie udostępnia bezpiecznego generatora identyfikatora operacji.'
  );
};

export interface CommunityThreadPageResult {
  threads: CommunityThread[];
  cursor: QueryDocumentSnapshot<DocumentData> | null;
  hasMore: boolean;
}

export interface CommunityThreadRead {
  threadId: string;
  categorySlug?: string | null;
  lastReadAt?: unknown;
  lastSeenPostId?: string | null;
}

export interface CommunityLatestActivityResult {
  categorySlug: string;
  threads: CommunityThread[];
  error: unknown | null;
}

export type CommunityModerationOutcomeAction =
  | 'post_hidden'
  | 'thread_hidden'
  | 'thread_restored'
  | 'thread_closed'
  | 'thread_reopened';

export type OwnCommunityNotificationOutcome =
  | {
      kind: 'community_report_resolved';
      subjectKind: 'thread' | 'post';
      resolutionStatus: 'reviewed' | 'dismissed' | 'action_taken';
      resolvedAtMillis: number;
    }
  | {
      kind: 'community_moderation_action';
      subjectKind: 'thread' | 'post';
      moderationAction: CommunityModerationOutcomeAction;
      occurredAtMillis: number;
    };

const mapThread = (docSnap: QueryDocumentSnapshot<DocumentData>): CommunityThread => ({
  id: docSnap.id,
  ...(docSnap.data() as Omit<CommunityThread, 'id'>),
});

const mapPost = (docSnap: QueryDocumentSnapshot<DocumentData>): CommunityPost => ({
  id: docSnap.id,
  ...(docSnap.data() as Omit<CommunityPost, 'id'>),
  quote: normalizeCommunityPostQuote(docSnap.data().quote),
});

const mapReport = (docSnap: QueryDocumentSnapshot<DocumentData>): CommunityReport => ({
  id: docSnap.id,
  ...(docSnap.data() as Omit<CommunityReport, 'id'>),
});

const mapReportOccurrence = (docSnap: {
  id: string;
  data: () => DocumentData;
}): CommunityReportOccurrence => ({
  id: docSnap.id,
  ...(docSnap.data() as Omit<CommunityReportOccurrence, 'id'>),
});

const timestampMillis = (value: unknown): number => {
  if (value && typeof (value as { toMillis?: unknown }).toMillis === 'function') {
    return (value as { toMillis: () => number }).toMillis();
  }
  return 0;
};

const sortThreads = (
  threads: CommunityThread[],
  field: 'lastActivityAt' | 'createdAt' | 'repliesCount',
  pinnedFirst = false
): CommunityThread[] =>
  [...threads].sort((first, second) => {
    if (pinnedFirst && first.isPinned !== second.isPinned) return first.isPinned ? -1 : 1;
    if (field === 'repliesCount') return (second.repliesCount || 0) - (first.repliesCount || 0);
    return timestampMillis(second[field]) - timestampMillis(first[field]);
  });

const callCommunityFunction = async <TInput, TOutput>(
  name: string,
  payload: TInput
): Promise<TOutput> => {
  const callable = httpsCallable<TInput, TOutput>(functions, name);
  const result = await callable(payload);
  return result.data;
};

const parseOwnCommunityNotificationOutcome = (value: unknown): OwnCommunityNotificationOutcome => {
  if (!value || typeof value !== 'object') {
    throw new Error('Nieprawidłowa odpowiedź wyniku zgłoszenia.');
  }
  const data = value as Record<string, unknown>;
  const isResolutionStatus =
    data.resolutionStatus === 'reviewed' ||
    data.resolutionStatus === 'dismissed' ||
    data.resolutionStatus === 'action_taken';
  if (data.kind === 'community_report_resolved') {
    if (
      (data.subjectKind !== 'thread' && data.subjectKind !== 'post') ||
      !isResolutionStatus ||
      typeof data.resolvedAtMillis !== 'number' ||
      !Number.isSafeInteger(data.resolvedAtMillis) ||
      data.resolvedAtMillis <= 0
    ) {
      throw new Error('Nieprawidłowa odpowiedź wyniku zgłoszenia.');
    }
    return data as unknown as OwnCommunityNotificationOutcome;
  }

  const moderationActions: CommunityModerationOutcomeAction[] = [
    'post_hidden',
    'thread_hidden',
    'thread_restored',
    'thread_closed',
    'thread_reopened',
  ];
  if (
    data.kind !== 'community_moderation_action' ||
    (data.subjectKind !== 'thread' && data.subjectKind !== 'post') ||
    !moderationActions.includes(data.moderationAction as CommunityModerationOutcomeAction) ||
    typeof data.occurredAtMillis !== 'number' ||
    !Number.isSafeInteger(data.occurredAtMillis) ||
    data.occurredAtMillis <= 0 ||
    (data.moderationAction === 'post_hidden' && data.subjectKind !== 'post') ||
    (data.moderationAction !== 'post_hidden' && data.subjectKind !== 'thread')
  ) {
    throw new Error('Nieprawidłowa odpowiedź wyniku moderacji.');
  }
  return data as unknown as OwnCommunityNotificationOutcome;
};

const getCategories = () => COMMUNITY_CATEGORIES.filter((category) => category.isActive);

const getPublicPreview = async (): Promise<{
  announcements: CommunityThread[];
  gratitude: CommunityThread[];
}> => {
  const snapshots = await Promise.all(
    (['announcements', 'gratitude'] as const).flatMap((categoryType) =>
      (['open', 'closed'] as const).map((status) =>
        getDocs(
          query(
            collection(firestore, THREADS_COLLECTION),
            where('isPublicPreviewEligible', '==', true),
            where('categoryType', '==', categoryType),
            where('status', '==', status),
            orderBy('lastActivityAt', 'desc'),
            limit(12)
          )
        )
      )
    )
  );
  const threads = sortThreads(
    snapshots
      .flatMap((snapshot) => snapshot.docs.map(mapThread))
      .filter((thread) => canViewCommunityThread(null, null, thread)),
    'lastActivityAt'
  ).slice(0, 12);
  return {
    announcements: threads.filter((item) => item.categoryType === 'announcements').slice(0, 5),
    gratitude: threads.filter((item) => item.categoryType === 'gratitude').slice(0, 6),
  };
};

const getLatestActivity = async (categorySlugs?: string[]): Promise<CommunityThread[]> => {
  const activeCategorySlugs =
    categorySlugs && categorySlugs.length > 0
      ? categorySlugs
      : getCategories().map((category) => category.slug);
  const snapshots = await Promise.all(
    activeCategorySlugs.flatMap((categorySlug) => {
      const categoryType = COMMUNITY_CATEGORY_BY_SLUG.get(categorySlug)?.type;
      return (['open', 'closed'] as const).map((status) =>
        getDocs(
          query(
            collection(firestore, THREADS_COLLECTION),
            where('categorySlug', '==', categorySlug),
            ...(categoryType ? [where('categoryType', '==', categoryType)] : []),
            where('status', '==', status),
            limit(6)
          )
        )
      );
    })
  );
  return sortThreads(
    snapshots.flatMap((snapshot) => snapshot.docs.map(mapThread)),
    'lastActivityAt'
  );
};

const getLatestActivityByCategory = async (
  categorySlugs: string[]
): Promise<CommunityLatestActivityResult[]> => {
  const activeCategorySlugs = categorySlugs.filter(Boolean);
  const results = await Promise.all(
    activeCategorySlugs.map(async (categorySlug) => {
      try {
        const threads = await getLatestActivity([categorySlug]);
        return {
          categorySlug,
          threads: threads.filter((thread) => thread.categorySlug === categorySlug),
          error: null,
        };
      } catch (error) {
        return {
          categorySlug,
          threads: [],
          error,
        };
      }
    })
  );
  return results;
};

const getThreadReads = async (userId: string): Promise<CommunityThreadRead[]> => {
  const normalizedUserId = String(userId || '').trim();
  if (!normalizedUserId) return [];
  const snapshot = await getDocs(
    collection(firestore, COMMUNITY_USER_READS_COLLECTION, normalizedUserId, 'threads')
  );
  return snapshot.docs.map((docSnap) => {
    const data = docSnap.data();
    return {
      threadId: String(data.threadId || docSnap.id),
      categorySlug: (data.categorySlug as string | null | undefined) || null,
      lastReadAt: data.lastReadAt,
      lastSeenPostId: (data.lastSeenPostId as string | null | undefined) || null,
    };
  });
};

const getThreadsByCategory = async (
  categorySlug: string,
  options: {
    pageSize?: number;
    cursor?: QueryDocumentSnapshot<DocumentData> | null;
    filter?: 'all' | 'open' | 'closed' | 'pinned';
    sort?: 'lastActivity' | 'newest' | 'replies';
  } = {}
): Promise<CommunityThreadPageResult> => {
  const pageSize = Math.max(1, Math.min(40, options.pageSize || 20));
  const statusFilter =
    options.filter === 'open'
      ? ['open']
      : options.filter === 'closed'
        ? ['closed']
        : ['open', 'closed'];
  const orderField =
    options.sort === 'newest'
      ? 'createdAt'
      : options.sort === 'replies'
        ? 'repliesCount'
        : 'lastActivityAt';
  const categoryType = COMMUNITY_CATEGORY_BY_SLUG.get(categorySlug)?.type;
  const snapshot = await getDocs(
    query(
      collection(firestore, THREADS_COLLECTION),
      where('categorySlug', '==', categorySlug),
      ...(categoryType ? [where('categoryType', '==', categoryType)] : []),
      statusFilter.length === 1
        ? where('status', '==', statusFilter[0])
        : where('status', 'in', statusFilter),
      ...(options.filter === 'pinned' ? [where('isPinned', '==', true)] : []),
      orderBy('isPinned', 'desc'),
      orderBy(orderField, 'desc'),
      orderBy(documentId(), 'desc'),
      ...(options.cursor ? [startAfter(options.cursor)] : []),
      limit(pageSize + 1)
    )
  );
  const hasMore = snapshot.docs.length > pageSize;
  const pageDocs = snapshot.docs.slice(0, pageSize);
  return {
    threads: pageDocs.map(mapThread),
    cursor: hasMore ? pageDocs[pageDocs.length - 1] || null : null,
    hasMore,
  };
};

const getThread = async (threadId: string): Promise<CommunityThread | null> => {
  const snap = await getDoc(doc(firestore, THREADS_COLLECTION, threadId));
  if (!snap.exists()) return null;
  return { id: snap.id, ...(snap.data() as Omit<CommunityThread, 'id'>) };
};

const getPosts = async (
  threadId: string,
  options: { includeModerated?: boolean } = {}
): Promise<CommunityPost[]> => {
  const postsQuery = query(
    collection(firestore, THREADS_COLLECTION, threadId, 'posts'),
    ...(options.includeModerated ? [] : [where('status', '==', 'visible')]),
    orderBy('createdAt', 'asc')
  );
  const snapshot = await getDocs(postsQuery);
  return snapshot.docs.map(mapPost);
};

const getReportsForAdmin = async (
  status: CommunityReport['status'] | 'all' = 'open'
): Promise<CommunityReport[]> => {
  const constraints =
    status === 'all'
      ? [orderBy('createdAt', 'desc'), limit(80)]
      : [where('status', '==', status), orderBy('createdAt', 'desc'), limit(80)];
  const reportsQuery = query(collection(firestore, REPORTS_COLLECTION), ...constraints);
  const snapshot = await getDocs(reportsQuery);
  return snapshot.docs.map(mapReport);
};

const getReportOccurrenceForAdmin = async (
  reportId: string,
  occurrenceId: string
): Promise<CommunityReportOccurrence | null> => {
  const normalizedReportId = String(reportId || '').trim();
  const normalizedOccurrenceId = String(occurrenceId || '').trim();
  if (!normalizedReportId || !normalizedOccurrenceId) return null;
  const snapshot = await getDoc(
    doc(firestore, REPORTS_COLLECTION, normalizedReportId, 'occurrences', normalizedOccurrenceId)
  );
  return snapshot.exists() ? mapReportOccurrence(snapshot) : null;
};

const getThreadsForAdmin = async (): Promise<CommunityThread[]> => {
  const threadsQuery = query(
    collection(firestore, THREADS_COLLECTION),
    orderBy('lastActivityAt', 'desc'),
    limit(80)
  );
  const snapshot = await getDocs(threadsQuery);
  return snapshot.docs.map(mapThread);
};

const getApprovedServiceProviderOptions = async (): Promise<
  Array<{ id: string; serviceName: string }>
> => {
  const providersQuery = query(
    collection(firestore, SERVICE_PROVIDERS_COLLECTION),
    where('verificationStatus', '==', 'approved'),
    where('isVerified', '==', true),
    limit(80)
  );
  const snapshot = await getDocs(providersQuery);
  return snapshot.docs
    .map((docSnap) => {
      const data = docSnap.data() as Partial<ServiceProvider>;
      return {
        id: docSnap.id,
        serviceName: String(data.serviceName || '').trim() || 'Serwis',
        isTestData: data.isTestData === true,
      };
    })
    .filter((item) => !item.isTestData)
    .map(({ id, serviceName }) => ({ id, serviceName }));
};

const createThread = async (payload: {
  categorySlug: string;
  title: string;
  body: string;
  clientRequestId: string;
  targetServiceProviderId?: string;
}): Promise<{ success: true; threadId: string; postId: string }> =>
  callCommunityFunction('createCommunityThread', payload);

const createPost = async (payload: {
  threadId: string;
  body: string;
  clientRequestId: string;
  quotePostId?: string;
}): Promise<{ success: true; threadId: string; postId: string }> => {
  const quotePostId = String(payload.quotePostId || '').trim();
  return callCommunityFunction('createCommunityPost', {
    threadId: payload.threadId,
    body: payload.body,
    clientRequestId: payload.clientRequestId,
    ...(quotePostId ? { quotePostId } : {}),
  });
};

const editContent = async (payload: {
  threadId: string;
  postId: string;
  title?: string;
  body?: string;
}): Promise<{ success: true; threadId: string; postId: string }> =>
  callCommunityFunction('editCommunityContent', payload);

const reportContent = async (payload: {
  targetType: 'thread' | 'post';
  threadId: string;
  postId?: string;
  reason: CommunityReportReason;
  message?: string;
  clientRequestId: string;
}): Promise<{ success: true; reportId: string; occurrenceId: string; occurrenceVersion: number }> =>
  callCommunityFunction<
    typeof payload,
    { success: true; reportId: string; occurrenceId: string; occurrenceVersion: number }
  >('reportCommunityContent', payload);

const moderate = async (payload: {
  action:
    | 'hide_post'
    | 'restore_post'
    | 'hide_thread'
    | 'restore_thread'
    | 'close_thread'
    | 'reopen_thread'
    | 'pin_thread'
    | 'unpin_thread'
    | 'resolve_report';
  threadId?: string;
  postId?: string;
  reportId?: string;
  status?: 'reviewed' | 'dismissed' | 'action_taken';
  note?: string;
  resolutionNote?: string;
  actionTaken?: string;
}): Promise<{ success: true }> => callCommunityFunction('moderateCommunityContent', payload);

const getOwnNotificationOutcome = async (
  notificationId: string
): Promise<OwnCommunityNotificationOutcome> => {
  const normalizedId = String(notificationId || '').trim();
  if (!/^[A-Za-z0-9_-]{1,256}$/.test(normalizedId)) {
    throw new Error('Nieprawidłowy identyfikator powiadomienia.');
  }
  const result = await callCommunityFunction<{ notificationId: string }, unknown>(
    'getOwnCommunityNotificationOutcome',
    { notificationId: normalizedId }
  );
  return parseOwnCommunityNotificationOutcome(result);
};

const markThreadRead = async (payload: {
  threadId: string;
  categorySlug?: string;
  lastSeenPostId?: string;
}): Promise<{ success: true; threadId: string }> =>
  callCommunityFunction('markCommunityThreadRead', payload);

export const communityService = {
  getCategories,
  getPublicPreview,
  getLatestActivity,
  getLatestActivityByCategory,
  getThreadReads,
  getThreadsByCategory,
  getThread,
  getPosts,
  getReportsForAdmin,
  getReportOccurrenceForAdmin,
  getThreadsForAdmin,
  getApprovedServiceProviderOptions,
  createThread,
  createPost,
  editContent,
  reportContent,
  moderate,
  getOwnNotificationOutcome,
  markThreadRead,
};
